import * as Sentry from '@sentry/vue'

/**
 * Error handling composable
 */
export function useErrorHandling() {

  //Process an error
  const processError = error => {

    //Ensure error in error processing doesn't trigger an error itself
    try {

      //No error? 🤨
      if (!error) {
        //Seems to originate from Sentry itself, according to this comment:
        // NOTE: If you are a Sentry user, and you are seeing this stack frame, it
        //       means the sentry.javascript SDK caught an error invoking your application code. This
        //       is expected behavior and NOT indicative of a bug with sentry.javascript
        Sentry.captureException(new Error('Error is empty'), {level: 'info'})
        return
      }

      //Ensure error is an object
      if (typeof error !== 'object') {
        Sentry.captureException(new Error(`Error is not an object: ${error}`), {level: 'info'})
        return
      }

      //Already processed?
      if (error.isProcessed) {
        return
      }

      //Log to console
      console.error(error)

      //Capture in Sentry
      if (!error.ignoreInSentry) {
        Sentry.captureException(error)
      }

      //Flag as processed
      error.isProcessed = true
    }
    catch {
      //Fall through
    }
  }

  //Register global event listeners
  const registerGlobalErrorListeners = () => {

    //Add event listener for general errors outside of Vue's scope
    window.addEventListener('error', event => {
      if (event instanceof PromiseRejectionEvent) {
        processError(event.reason)
      }
      else {
        processError(event.error)
      }
    })

    //Add event listener for unhandled rejections
    window.addEventListener('unhandledrejection', event => {
      if (event.reason && !event.reason.ignoreUnhandledRejection) {
        processError(event.reason)
      }
    })
  }

  //Capture a message in Sentry
  const captureMessage = message => {
    console.log(message)
    Sentry.captureMessage(message)
  }

  //Expose interface
  return {
    processError,
    registerGlobalErrorListeners,
    captureMessage,
  }
}
